import * as React from 'react'
import type { CountdownRenderProps } from 'react-countdown'
import { default as ReactCountdown } from 'react-countdown'
import { useIsMounted } from 'hooks/use-is-mounted'
import { cx } from 'utils/classnames'
import { useTranslations } from 'next-intl'
import { formatCountdownParts } from 'utils/time'

import { minutesBetweenDates } from '../../utils/date'

interface Props {
  onComplete: () => void
  countdown: string
  prefix?: string
  daysInMinutes?: boolean
  style?: React.CSSProperties
  className?: string
}

export function Countdown({
  countdown,
  prefix,
  onComplete,
  daysInMinutes,
  style,
  className,
}: Props) {
  const t = useTranslations('cf_banners')
  if (!countdown) {
    return null
  }

  const now = new Date()
  const countdownDate = new Date(countdown)
  const minutes = minutesBetweenDates(now, countdownDate)

  if (minutes <= 0) {
    return null
  }

  return (
    <CountdownComponent
      date={countdownDate}
      prefix={prefix ?? t('countdown')}
      separator={' : '}
      onComplete={() => {
        onComplete()
      }}
      daysInMinutes={daysInMinutes ?? false}
      style={style}
      className={className}
    />
  )
}

interface DateFormatProps {
  day: string
  hour: string
  minute: string
  second: string
}

interface CountdownProps {
  className?: string
  date: Date | string | number
  prefix?: string
  isEmphasized?: boolean
  style?: React.CSSProperties
  separator?: string
  onComplete?: VoidFunction
  dateFormat?: DateFormatProps
  isBoxed?: boolean
  daysInMinutes?: boolean
}

export const CountdownComponent = (props: CountdownProps) => {
  const {
    className,
    prefix,
    style,
    separator = ' ',
    dateFormat = { day: 'd', hour: 'h', minute: 'm', second: 's' },
    daysInMinutes = false,
    ...countdownProps
  } = props
  const isMounted = useIsMounted()

  const renderCountdown = ({ days, hours, minutes, seconds }: CountdownRenderProps) => {
    const daysValue = isMounted ? days : 0
    const hoursValue = isMounted ? hours : 0
    const minutesValue = isMounted ? minutes : 0
    const secondsValue = isMounted ? seconds : 0

    const formattedCountdown = formatCountdownParts(
      {
        days: daysValue,
        hours: hoursValue,
        minutes: minutesValue,
        seconds: secondsValue,
      },
      {
        separator,
        labels: dateFormat,
        compressDaysIntoMinutes: Boolean(daysInMinutes),
        padHoursMinutesSeconds: false,
      },
    )

    return (
      <p className={cx('whitespace-nowrap text-xs font-thin', className)} style={style}>
        {prefix ? `${prefix} ` : null}
        <span className="font-medium">{formattedCountdown}</span>
      </p>
    )
  }

  return <ReactCountdown renderer={renderCountdown} {...countdownProps} />
}
